import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClinicStatus } from '../enums/clinic-status.enum';
import { ClinicService } from './clinic.service';
import { UserManagementService } from './user-management.service';

@Injectable({ providedIn: 'root' })
export class ClinicStatusService {
  private readonly statusSubject = new BehaviorSubject<ClinicStatus | null>(null);
  private readonly suspensionCommentsSubject = new BehaviorSubject<string | null>(null);
  private readonly hasClinicSubject = new BehaviorSubject<boolean>(false);
  private loaded = false;

  readonly isClinicActive$: Observable<boolean> = this.statusSubject
    .asObservable()
    .pipe(map((status) => status === ClinicStatus.ACTIVE));

  readonly status$: Observable<ClinicStatus | null> = this.statusSubject.asObservable();
  readonly suspensionComments$: Observable<string | null> =
    this.suspensionCommentsSubject.asObservable();

  /** True when the doctor belongs to at least one clinic. */
  readonly hasClinic$: Observable<boolean> = this.hasClinicSubject.asObservable();

  constructor(
    private clinicService: ClinicService,
    private userManagementService: UserManagementService,
  ) {}

  load(): void {
    if (this.loaded) return;
    this.loaded = true;
    this.clinicService.getClinicStatus().subscribe({
      next: (response) => {
        this.statusSubject.next(response.status);
        this.suspensionCommentsSubject.next(response.suspensionComments?.trim() || null);
      },
      error: () => {
        this.statusSubject.next(null);
        this.suspensionCommentsSubject.next(null);
      },
    });
    
    if (localStorage.getItem('userRole') !== 'DOCTOR') {
      this.hasClinicSubject.next(true);
    } else {
      this.userManagementService.getClinicMemberships().subscribe({
        next: (memberships) => this.hasClinicSubject.next(memberships.length > 0),
        error: () => this.hasClinicSubject.next(false),
      });
    }
  }

  isActive(): boolean {
    return this.statusSubject.getValue() === ClinicStatus.ACTIVE;
  }

  getStatus(): ClinicStatus | null {
    return this.statusSubject.getValue();
  }

  /** Force a fresh fetch — call this immediately after switching the active clinic. */
  refresh(): void {
    this.loaded = false;
    this.load();
  }
}
